const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

/* ==========================================================================
 * NORMALISATION D'UN ABONNEMENT / D'UNE PRESTATION
 * ========================================================================== */

export const normalizeSubscription = (subscription = {}) => {
  const totalAmount = toNumber(subscription.total_amount);

  const amountPaid = toNumber(subscription.amount_paid);

  const remainingAmount =
    subscription.remaining_amount !== undefined
      ? toNumber(subscription.remaining_amount)
      : Math.max(totalAmount - amountPaid, 0);

  const itemType =
    subscription.item_type ||
    (subscription.periodicity === "ponctuel" ? "one_time" : "subscription");

  return {
    ...subscription,

    item_type: itemType,

    periodicity:
      subscription.periodicity ||
      (itemType === "one_time" ? "ponctuel" : "annuel"),

    renewal_status:
      subscription.renewal_status ||
      (itemType === "one_time" ? "not_applicable" : "not_started"),

    total_amount: totalAmount,

    amount_paid: amountPaid,

    remaining_amount: remainingAmount,

    payment_status: subscription.payment_status || "pending",

    is_overdue: Boolean(subscription.is_overdue),

    is_paid: Boolean(subscription.is_paid),

    days_until_end:
      subscription.days_until_end === null ||
      subscription.days_until_end === undefined
        ? null
        : Number(subscription.days_until_end),
  };
};

/* ==========================================================================
 * NORMALISATION D'UNE LISTE
 * ========================================================================== */

export const normalizeSubscriptions = (subscriptions) =>
  asArray(subscriptions).map(normalizeSubscription);

/* ==========================================================================
 * NORMALISATION DU DASHBOARD
 * ========================================================================== */

export const normalizeSubscriptionDashboard = (response = {}) => ({
  generatedAt: response.generated_at || null,

  /* ------------------------------------------------------------------------
   * FILTRES
   * ---------------------------------------------------------------------- */

  filters: {
    months: Number(response?.filters?.months || 12),

    renewalDays: Number(response?.filters?.renewal_days || 30),

    renewalPreparationDays: Number(
      response?.filters?.renewal_preparation_days || 15,
    ),

    renewalAnticipationDays: Number(
      response?.filters?.renewal_anticipation_days || 60,
    ),

    quoteFollowUpDays: Number(response?.filters?.quote_follow_up_days || 7),

    product: response?.filters?.history_product || "all",
  },

  /* ------------------------------------------------------------------------
   * PRODUITS DISPONIBLES
   * ---------------------------------------------------------------------- */

  availableProducts: asArray(response.available_products)
    .map((product) => String(product || "").trim())
    .filter(Boolean),

  /* ------------------------------------------------------------------------
   * RÉSUMÉ / KPI
   * ---------------------------------------------------------------------- */

  summary: {
    activeClientsCount: Number(response?.summary?.active_clients_count || 0),

    activeSubscriptionsCount: Number(
      response?.summary?.active_subscriptions_count || 0,
    ),

    activeAnnualRevenue: Number(response?.summary?.active_annual_revenue || 0),

    renewalsToPrepareCount: Number(
      response?.summary?.renewals_to_prepare_count || 0,
    ),

    urgentActionsCount: Number(response?.summary?.urgent_actions_count || 0),

    forecast12Months: Number(response?.summary?.forecast_12_months || 0),

    /*
     * KPI historiques conservés
     * pour compatibilité avec
     * l'interface actuelle.
     */

    newClientsThisMonth: Number(response?.summary?.new_clients_this_month || 0),

    subscriptionsSignedThisMonth: Number(
      response?.summary?.subscriptions_signed_this_month || 0,
    ),

    oneTimeSalesThisMonth: Number(
      response?.summary?.one_time_sales_this_month || 0,
    ),

    signedRevenueThisMonth: Number(
      response?.summary?.signed_revenue_this_month || 0,
    ),

    upcomingRenewalsCount: Number(
      response?.summary?.upcoming_renewals_count || 0,
    ),

    overduePaymentsCount: Number(
      response?.summary?.overdue_payments_count || 0,
    ),

    overdueAmount: Number(response?.summary?.overdue_amount || 0),

    quotesToFollowUpCount: Number(
      response?.summary?.quotes_to_follow_up_count || 0,
    ),

    expiredSubscriptionsCount: Number(
      response?.summary?.expired_subscriptions_count || 0,
    ),

    missingDataCount: Number(response?.summary?.missing_data_count || 0),
  },

  /* ------------------------------------------------------------------------
   * DÉTAIL DES KPI HISTORIQUES
   * ---------------------------------------------------------------------- */

  summaryDetails: {
    newClientsThisMonth: normalizeSubscriptions(
      response?.summary_details?.new_clients_this_month,
    ),

    signedSalesThisMonth: normalizeSubscriptions(
      response?.summary_details?.signed_sales_this_month,
    ),
  },

  /* ------------------------------------------------------------------------
   * PRIORITÉS
   * ---------------------------------------------------------------------- */

  priorities: asArray(response.priorities).map((priority) => ({
    ...priority,

    priority: Number(priority.priority || 99),

    subscription: normalizeSubscription(priority.subscription || {}),
  })),

  /* ------------------------------------------------------------------------
   * PIPELINE DE RENOUVELLEMENT
   * ---------------------------------------------------------------------- */

  renewalPipeline: {
    anticipate: normalizeSubscriptions(response?.renewal_pipeline?.anticipate),

    toPrepare: normalizeSubscriptions(response?.renewal_pipeline?.to_prepare),

    quoteSent: normalizeSubscriptions(response?.renewal_pipeline?.quote_sent),

    followUp: normalizeSubscriptions(response?.renewal_pipeline?.follow_up),

    accepted: normalizeSubscriptions(response?.renewal_pipeline?.accepted),

    renewed: normalizeSubscriptions(response?.renewal_pipeline?.renewed),
  },

  /* ------------------------------------------------------------------------
   * TIMELINE DES RENOUVELLEMENTS
   * ---------------------------------------------------------------------- */

  renewalTimeline: asArray(response.renewal_timeline).map((month) => ({
    ...month,

    count: Number(month.count || 0),

    amount: Number(month.amount || 0),

    items: normalizeSubscriptions(month.items),
  })),

  /* ------------------------------------------------------------------------
   * PARC CLIENTS / PRODUITS
   * ---------------------------------------------------------------------- */

  productPortfolio: asArray(response.product_portfolio).map((family) => ({
    ...family,

    active_clients: Number(family.active_clients || 0),

    active_subscriptions: Number(family.active_subscriptions || 0),

    annual_revenue: Number(family.annual_revenue || 0),

    products: asArray(family.products).map((product) => ({
      ...product,

      active_clients: Number(product.active_clients || 0),

      active_subscriptions: Number(product.active_subscriptions || 0),

      annual_revenue: Number(product.annual_revenue || 0),
    })),
  })),

  /* ------------------------------------------------------------------------
   * PRÉVISIONS
   * ---------------------------------------------------------------------- */

  forecast: {
    assumption: response?.forecast?.assumption || "",

    monthsCount: Number(response?.forecast?.months_count || 12),

    totalPotentialRevenue: Number(
      response?.forecast?.total_potential_revenue || 0,
    ),

    totalAcceptedRevenue: Number(
      response?.forecast?.total_accepted_revenue || 0,
    ),

    months: asArray(response?.forecast?.months).map((month) => ({
      ...month,

      renewal_count: Number(month.renewal_count || 0),

      potential_revenue: Number(month.potential_revenue || 0),

      accepted_revenue: Number(month.accepted_revenue || 0),

      items: normalizeSubscriptions(month.items),
    })),
  },

  /* ------------------------------------------------------------------------
   * HISTORIQUE
   *
   * Conservé pendant la transition
   * afin de ne pas casser
   * SubscriptionHistoryChart.
   * ---------------------------------------------------------------------- */

  history: asArray(response.history).map((row) => ({
    ...row,

    existing_clients: Number(row.existing_clients || 0),

    new_clients: Number(row.new_clients || 0),

    active_clients: Number(row.active_clients || 0),

    previous_year_active_clients: Number(row.previous_year_active_clients || 0),

    client_change_vs_previous_year: Number(
      row.client_change_vs_previous_year || 0,
    ),

    client_change_rate_vs_previous_year:
      row.client_change_rate_vs_previous_year === null ||
      row.client_change_rate_vs_previous_year === undefined
        ? null
        : Number(row.client_change_rate_vs_previous_year),

    signed_subscriptions: Number(row.signed_subscriptions || 0),

    one_time_sales: Number(row.one_time_sales || 0),

    signed_sales: Number(row.signed_sales || 0),

    signed_revenue: Number(row.signed_revenue || 0),

    cumulative_clients: Number(row.cumulative_clients || 0),

    cumulative_subscriptions: Number(row.cumulative_subscriptions || 0),

    cumulative_sales: Number(row.cumulative_sales || 0),

    cumulative_revenue: Number(row.cumulative_revenue || 0),
  })),

  /* ------------------------------------------------------------------------
   * ANCIEN CENTRE D'ACTIONS
   *
   * Conservé pour que l'interface actuelle
   * continue de fonctionner pendant la refonte.
   * ---------------------------------------------------------------------- */

  actions: {
    upcomingRenewals: normalizeSubscriptions(
      response?.actions?.upcoming_renewals,
    ),

    overduePayments: normalizeSubscriptions(
      response?.actions?.overdue_payments,
    ),

    quotesToFollowUp: normalizeSubscriptions(
      response?.actions?.quotes_to_follow_up,
    ),

    expiredSubscriptions: normalizeSubscriptions(
      response?.actions?.expired_subscriptions,
    ),

    missingData: normalizeSubscriptions(response?.actions?.missing_data),
  },
});
